/**
 * AdminBookClubTab — admin-only UI to pick this week's featured Book +
 * Audio for the Book Club rooms. Built Jun 4 2026 in response to the
 * user's "Wire correct part to library book club for admin. The current
 * part is for episode comment" request.
 *
 * Each slot ("Book of the Week", "Audio of the Week") shows the current
 * pick, a clear button, a search-as-you-type composer that searches the
 * admin's existing /api/admin/content list, and a list of the last 30
 * historical picks.
 *
 * Co-admins (read-only): see the picks + history, but the Pin/Clear
 * buttons are hidden.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  BookMarked, Headphones, Loader2, Pin, Trash2, Check, Search, ChevronDown,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { authFetch } from '../utils/authUtils';
import { resolveCoverUrl } from '../utils/coverUrl';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SLOTS = [
  {
    id: 'book_of_the_week',
    label: 'Book of the Week',
    Icon: BookMarked,
    hint: 'Pick one ebook to anchor this week\'s reading-room discussion.',
    accept: ['ebook', 'text'],
    accent: 'amber',
  },
  {
    id: 'audio_of_the_week',
    label: 'Audio of the Week',
    Icon: Headphones,
    hint: 'Pick one audiobook or cinematic series for the listening room.',
    accept: ['audio', 'audio_single', 'audiobook', 'cinematic'],
    accent: 'emerald',
  },
];

const ACCENTS = {
  amber: 'from-amber-500/15 to-amber-500/5 border-amber-400/40 text-amber-200',
  emerald: 'from-emerald-500/15 to-emerald-500/5 border-emerald-400/40 text-emerald-200',
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return '—'; }
};

/**
 * Picker — search-as-you-type combobox over /api/admin/content. We filter
 * client-side by the `accept` list because the admin content endpoint
 * already returns the full library in one hit.
 */
const ContentPicker = ({ accept, onPick, busy }) => {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [content, setContent] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        // Use the public /library endpoint — it returns books + series +
        // standalone_books which collectively cover every kind we accept.
        // /api/admin/content doesn't exist (404), but /api/library does
        // and is already role-aware for the admin caller.
        const r = await authFetch(`${BACKEND_URL}/api/library`);
        if (!r.ok) return;
        const d = await r.json();
        if (cancelled) return;
        const books = (d.books || []).map((b) => ({
          ...b,
          ref_id: b.book_id || b.content_id,
          content_type: b.content_type || b.type || 'ebook',
        }));
        const series = (d.series || []).map((s) => ({
          ...s,
          ref_id: s.series_id || s.book_id,
          content_type: s.content_type || s.type
            || (s.series_kind === 'cinematic' ? 'cinematic' : 'audiobook'),
        }));
        const standalone = (d.standalone_books || []).map((b) => ({
          ...b,
          ref_id: b.book_id || b.content_id,
          content_type: b.type || 'audiobook',
        }));
        setContent([...books, ...series, ...standalone]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const candidates = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (content || []).filter((c) => {
      const kind = (c.content_type || c.type || '').toLowerCase();
      if (!accept.includes(kind)) return false;
      if (!q) return true;
      return (c.title || '').toLowerCase().includes(q)
        || (c.author || '').toLowerCase().includes(q);
    }).slice(0, 12);
  }, [content, query, accept]);

  return (
    <div className="relative">
      <div className="flex items-center gap-2 rounded-lg bg-slate-900 border border-slate-700 px-2.5 py-2 focus-within:border-indigo-400">
        <Search className="w-4 h-4 text-slate-400 shrink-0" />
        <input
          type="text"
          placeholder={`Search ${accept.join(' / ')}…`}
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          className="flex-1 bg-transparent text-sm text-white placeholder-slate-500 focus:outline-none"
          data-testid="bookclub-picker-input"
        />
        <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </div>
      {open && (
        <div className="absolute z-30 left-0 right-0 mt-1 max-h-72 overflow-y-auto rounded-lg bg-slate-900 border border-slate-700 shadow-2xl">
          {loading && (
            <p className="text-xs text-slate-400 p-3"><Loader2 className="w-3 h-3 inline-block animate-spin mr-1" /> Loading library…</p>
          )}
          {!loading && candidates.length === 0 && (
            <p className="text-xs text-slate-500 p-3">No matches.</p>
          )}
          {!loading && candidates.map((c) => {
            const refId = c.ref_id || c.content_id || c.book_id || c.series_id;
            return (
              <button
                key={refId}
                type="button"
                disabled={busy}
                onClick={() => {
                  onPick(refId);
                  setOpen(false);
                  setQuery('');
                }}
                className="w-full text-left flex items-center gap-2 px-2.5 py-2 hover:bg-slate-800 disabled:opacity-50 border-b border-slate-800 last:border-b-0"
                data-testid={`bookclub-pick-${refId}`}
              >
                <img
                  src={resolveCoverUrl(c.cover_image)}
                  alt=""
                  className="w-8 h-10 object-cover rounded shrink-0 bg-slate-800"
                  onError={(ev) => { ev.currentTarget.style.visibility = 'hidden'; }}
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-white font-semibold truncate">{c.title || 'Untitled'}</p>
                  <p className="text-[11px] text-slate-400 truncate">
                    {(c.content_type || c.type || '—').toUpperCase()} · {c.author || '—'}
                  </p>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

const Slot = ({ slot, pick, history, onPin, onClear, readOnly, busy }) => {
  const Icon = slot.Icon;
  const accentCls = ACCENTS[slot.accent] || ACCENTS.amber;
  const slotHistory = (history || []).filter((h) => h.slot === slot.id).slice(0, 5);
  return (
    <section
      className={`rounded-2xl bg-gradient-to-br ${accentCls} border p-4 space-y-3`}
      data-testid={`bookclub-slot-${slot.id}`}
    >
      <header className="flex items-center gap-2">
        <Icon className="w-5 h-5" />
        <h3 className="text-base font-bold text-white">{slot.label}</h3>
      </header>
      <p className="text-xs opacity-80">{slot.hint}</p>

      {pick ? (
        <div className="rounded-xl bg-slate-900/70 border border-slate-700 p-3 flex items-center gap-3">
          <img
            src={resolveCoverUrl(pick.cover_image)}
            alt=""
            className="w-12 h-16 object-cover rounded shrink-0 bg-slate-800"
            onError={(ev) => { ev.currentTarget.style.visibility = 'hidden'; }}
          />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-white truncate" data-testid={`bookclub-pick-title-${slot.id}`}>
              {pick.title}
            </p>
            <p className="text-[11px] text-slate-400 truncate">
              by {pick.author_name || 'Unknown'} · pinned {fmtDate(pick.picked_at)}
            </p>
            <p className="text-[10px] text-slate-500 font-mono mt-0.5 truncate">{pick.ref_id}</p>
          </div>
          {!readOnly && (
            <button
              type="button"
              onClick={() => onClear(slot.id)}
              disabled={busy}
              className="p-2 rounded-md text-rose-300 hover:bg-rose-500/10 hover:text-rose-200 disabled:opacity-50"
              data-testid={`bookclub-clear-${slot.id}`}
              title="Clear this pick"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      ) : (
        <p className="text-xs italic text-white/60">No pick set — the room will read "No active feature yet".</p>
      )}

      {!readOnly && (
        <ContentPicker
          accept={slot.accept}
          busy={busy}
          onPick={(refId) => onPin(slot.id, refId)}
        />
      )}

      {slotHistory.length > 0 && (
        <details className="text-xs text-white/70" data-testid={`bookclub-history-${slot.id}`}>
          <summary className="cursor-pointer hover:text-white">Recent history ({slotHistory.length})</summary>
          <ul className="mt-2 space-y-1">
            {slotHistory.map((h) => (
              <li key={`${h.ref_id}-${h.picked_at}`} className="truncate">
                <span className="text-white/60">{fmtDate(h.picked_at)}</span>
                {' · '}
                <span className="text-white">{h.title}</span>
                {' · '}
                <span className="text-white/60 italic">{h.active ? 'active' : 'deactivated'}</span>
              </li>
            ))}
          </ul>
        </details>
      )}
    </section>
  );
};

const AdminBookClubTab = ({ role = 'admin' }) => {
  const readOnly = role !== 'admin';
  const [picks, setPicks] = useState({ book_of_the_week: null, audio_of_the_week: null });
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    try {
      const [pr, hr] = await Promise.all([
        authFetch(`${BACKEND_URL}/api/bookclub/featured`),
        authFetch(`${BACKEND_URL}/api/admin/bookclub/featured/history`),
      ]);
      if (pr.ok) setPicks(await pr.json());
      if (hr.ok) {
        const d = await hr.json();
        setHistory(d.history || []);
      }
    } catch (e) {
      // non-fatal
    }
  };

  useEffect(() => { refresh(); }, []);

  const pin = async (slot, refId) => {
    setBusy(true);
    const tid = toast.loading('Pinning…');
    try {
      const r = await authFetch(`${BACKEND_URL}/api/admin/bookclub/featured`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ slot, ref_id: refId }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail || 'Pin failed');
      toast.success('Pinned', { id: tid });
      refresh();
    } catch (e) {
      toast.error(e.message || 'Pin failed', { id: tid });
    } finally {
      setBusy(false);
    }
  };

  const clear = async (slot) => {
    if (!window.confirm('Clear this featured pick? Members will see "No active feature yet" until you pick another.')) return;
    setBusy(true);
    const tid = toast.loading('Clearing…');
    try {
      const r = await authFetch(`${BACKEND_URL}/api/admin/bookclub/featured/${slot}`, { method: 'DELETE' });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail || 'Clear failed');
      toast.success('Cleared', { id: tid });
      refresh();
    } catch (e) {
      toast.error(e.message || 'Clear failed', { id: tid });
    } finally {
      setBusy(false);
    }
  };

  // Force the weekly auto-rotation now. Clears any active picks and
  // re-runs the engagement-based candidate algorithm so the slots are
  // re-populated with the current week's winners.
  const autoRotate = async () => {
    if (!window.confirm(
      'Run the weekly auto-rotation now? This will replace any active picks with the most-discussed ebook + most-listened audio of the last 7 days.',
    )) return;
    setBusy(true);
    const tid = toast.loading('Rotating…');
    try {
      const r = await authFetch(`${BACKEND_URL}/api/admin/bookclub/featured/auto-rotate`, {
        method: 'POST',
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail || 'Rotation failed');
      toast.success('Auto-rotation complete', { id: tid });
      refresh();
    } catch (e) {
      toast.error(e.message || 'Rotation failed', { id: tid });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-bookclub-tab">
      <header className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-lg font-bold text-white">Book Club featured picks</h2>
          <p className="text-xs text-slate-400">
            One pin per slot · rooms read the latest active pick · auto-rotates Sundays 00:00 UK · co-admins are read-only.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {readOnly && (
            <span className="text-[10px] uppercase tracking-widest font-bold text-cyan-300 bg-cyan-500/10 border border-cyan-500/40 px-2 py-1 rounded-full">
              Read-only
            </span>
          )}
          {!readOnly && (
            <button
              type="button"
              onClick={autoRotate}
              disabled={busy}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white text-xs font-bold"
              data-testid="bookclub-auto-rotate-now"
              title="Force the weekly auto-rotation immediately"
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Pin className="w-3.5 h-3.5" />}
              Run auto-rotate now
            </button>
          )}
        </div>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {SLOTS.map((slot) => (
          <Slot
            key={slot.id}
            slot={slot}
            pick={picks?.[slot.id] || null}
            history={history}
            onPin={pin}
            onClear={clear}
            readOnly={readOnly}
            busy={busy}
          />
        ))}
      </div>
    </div>
  );
};

export default AdminBookClubTab;
